import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "./api";

const AuthContext = createContext(null);

// Preferenze del DISPOSITIVO (non dell'utenza): sopravvivono a logout/cambio account
const DEVICE_KEYS = new Set(["atlas_lang", "atlas_currency", "atlas_cookies", "atlas_a11y"]);

/** Rimuove ogni traccia dell'utenza precedente dal browser (token, sessione chat
 *  supporto, banner referral per-utente, ecc.), così logout/login e cambio account
 *  sullo stesso browser non si sovrappongono mai. Restano solo le preferenze device. */
export function clearUserStorage() {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith("atlas_") && !DEVICE_KEYS.has(k) && k !== "atlas_ref")
      .forEach((k) => localStorage.removeItem(k));
    sessionStorage.clear();
  } catch { /* storage non disponibile */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // Pulizia SOLO se il server risponde 401/403 (token davvero invalido/scaduto):
      // un errore di rete transitorio non deve buttare fuori l'utente.
      if (err?.response?.status === 401 || err?.response?.status === 403) clearUserStorage();
      setUser(null);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) return;
    checkAuth();
  }, [checkAuth]);

  const completeTwoFactor = useCallback((data) => {
    // accesso completato DOPO il secondo fattore: stessa pulizia del login normale
    clearUserStorage();
    localStorage.setItem("atlas_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const loginEmail = async (email, password, lang) => {
    const { data } = await api.post("/auth/login", { email, password, lang });
    // 2FA: password corretta ma serve il secondo fattore (o l'attivazione obbligatoria
    // per admin): NIENTE token salvato finché la verifica non è completa
    if (data.requires_2fa || data.requires_2fa_setup) return data;
    clearUserStorage(); // azzera l'eventuale utenza precedente prima del nuovo accesso
    localStorage.setItem("atlas_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const registerEmail = async (name, email, password, termsAccepted, lang, turnstileToken, antibot = {}, consents = {}) => {
    const { data } = await api.post("/auth/register", { name, email, password, terms_accepted: !!termsAccepted, age_confirmed: !!consents.age, marketing_opt_in: !!consents.marketing, bonus_code: consents.bonus?.trim() || undefined, lang, referral_code: localStorage.getItem("atlas_ref") || undefined, turnstile_token: turnstileToken, ...antibot });
    clearUserStorage();
    localStorage.removeItem("atlas_ref");
    localStorage.setItem("atlas_token", data.token);
    setUser(data.user);
    return { ...data.user, bonus_credited: data.bonus_credited };
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* cookie may already be gone */
    }
    clearUserStorage(); // pulizia completa: nessuna sovrapposizione con il prossimo accesso
    try {
      // DOPO la pulizia (che cancella gli atlas_*): blocca il Google One Tap fino al
      // prossimo login esplicito, altrimenti rientrerebbe da solo un secondo dopo
      localStorage.setItem("atlas_no_onetap", "1");
    } catch { /* storage non disponibile */ }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loginEmail, registerEmail, logout, checkAuth, completeTwoFactor }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
